/**
 * Shell copy for `src/auth` (keys: `shell.auth.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  protectedRoute: {
    signingIn: 'Signing in…',
    sessionExpiredSignInAgainTo: 'Session expired. Sign in again to continue.',
    signInAgain: 'Sign in again',
  },
  noKarexiaAccess: {
    title: 'This account has no access to Karexia',
    signedInAs: 'Signed in as {{email}}',
    explanation:
      'This account exists on the shared sign-in, but it has not been given access to Karexia.',
    invitationHint:
      'If a Karexia organisation invited you, open the invitation link from your email: it will grant you access. Otherwise, sign in with another account.',
    signOut: 'Sign out',
    useAnotherAccount: 'Use another account',
    invitationsTitle: 'Pending invitation',
    invitationsIntro: 'A Karexia organisation invited you. Accept the invitation to join it.',
    invitationRole: 'Role: {{role}}',
    acceptInvitation: 'Accept invitation',
    invitationUnverifiedEmail:
      'An invitation is waiting for you: open the link from your email to join (your email address is not verified yet).',
    loadingStatus: 'Loading your access options…',
    statusUnavailable: 'Your access options cannot be loaded right now.',
    createTitle: 'Create my workspace',
    createIntro: 'Create your own Karexia organisation: you will be its owner.',
    createSecondary: 'Would you rather create your own organisation than join the one that invited you?',
    orgNameLabel: 'Organisation name',
    orgNamePlaceholder: 'E.g. Lumière Studio',
    orgNameHint: 'Between 2 and 100 characters.',
    acceptTermsPrefix: 'I accept the',
    termsLink: 'terms of service',
    acceptTermsAnd: 'and the',
    privacyLink: 'privacy policy',
    marketingOptIn:
      'I agree to receive news, tips and offers from Karexia by email. I can unsubscribe at any time.',
    submit: 'Create my workspace',
    submitting: 'Creating…',
    confirmSkipInvitations:
      'An invitation is waiting for you. Do you really want to create a new organisation instead of accepting it?',
    refreshing: 'Workspace created! Updating your session…',
    errorPendingInvitation: 'An invitation to an existing organisation is waiting for you: accept it first, or confirm creating a new workspace.',
    errorAlreadyHasWorkspace: 'This account is already linked to a Karexia organisation. Sign in again to access it.',
    errorInvalidOrgName: 'The organisation name must be between 2 and 100 characters.',
    errorTermsNotAccepted: 'You must accept the terms of service and the privacy policy.',
    errorMarketingWordingRequired: 'The marketing consent could not be recorded. Please try again.',
    errorRateLimited: 'Too many attempts. Try again in a moment.',
    errorSelfServeUnavailable: 'Workspace creation is not available right now.',
    errorSelfServeDisabled: 'Workspace creation is disabled.',
    errorTenantProvisioningFailed: 'The workspace could not be created. Please try again.',
    errorIdentityProviderUnavailable: 'Access could not be granted yet. Please try again.',
    errorNetwork: 'Service temporarily unavailable. Check your connection and try again.',
    errorUnknown: 'Something went wrong. Please try again.',
  },
  companySso: {
    navEntry: 'Company SSO',
    title: 'Sign in with your company SSO',
    intro: 'Enter your work email: we will send you to your company’s sign-in.',
    emailLabel: 'Work email',
    emailPlaceholder: 'first.last@company.com',
    continue: 'Continue',
    searching: 'Searching…',
    notFound: 'No company SSO is configured for this domain. Use the regular sign-in.',
    regularSignIn: 'Regular sign-in',
    rateLimited: 'Too many attempts. Try again in a minute.',
    error: 'The lookup is unavailable right now. Please try again.',
    invalidEmail: 'Enter a valid email address.',
    backHome: 'Back to home',
  },
};

export default en;
